import { Dashboard } from "@/components/dashboard/Dashboard/Dashboard";
import { VStack } from "@/components/layout/VStack";
import { HStack } from "@/components/layout/HStack";
import { useEffect, useState } from "react";
import { useExplorations } from "../hooks/explorations/useExplorations";
import { Explorations } from "../components/explorations/Explorations";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { BookOpen } from "lucide-react";
import { EditExplorationSheet } from "../components/explorations/EditExplorationSheet";
import { EnrollStudentsSheet } from "../components/explorations/EnrollStudentsSheet";
import { ArchiveToggleButton } from "@/components/elements/ArchiveToggleButton/ArchiveToggleButton";

const AdminExplorations = ({
  archived = false,
}: {
  archived?: boolean;
  sysAdmin?: boolean;
}) => {
  const [open, setOpen] = useState<{
    edit: {
      open: boolean;
      exploration: any;
    };
    enroll: {
      open: boolean;
      exploration: any;
    };
  }>({
    edit: { open: false, exploration: undefined },
    enroll: { open: false, exploration: undefined },
  });
  const [explorations, setExplorations] = useState<{
    explorations: any[];
    classes: any[];
    rotations: any[];
    sessions: any[];
    teachers: any[];
  }>({
    explorations: [],
    classes: [],
    rotations: [],
    sessions: [],
    teachers: [],
  });
  const [students, setStudents] = useState<any[]>([]);

  const { loadExplorations, loading } = useExplorations({ archived });

  useEffect(() => {
    loadExplorations(setExplorations);
  }, [archived]);

  const openEdit = (explorationId: string) => {
    const exploration = explorations.explorations.find(
      (e) => e._id === explorationId,
    );
    const rotationId = explorations.sessions.find(
      (s) => s._id === exploration.session,
    ).rotation;

    setOpen((prev) => ({
      ...prev,
      edit: {
        open: true,
        exploration: {
          ...exploration,
          rotation: rotationId,
        },
      },
    }));
  };

  const openEnroll = (explorationId: string) => {
    const exploration = explorations.explorations.find(
      (e) => e._id === explorationId,
    );

    setOpen((prev) => ({
      ...prev,
      enroll: {
        open: true,
        exploration,
      },
    }));
  };

  return (
    <Dashboard activePage={archived ? "/explorations/archive" : "/explorations"}>
      <HStack justify="space-between" wrap>
        <VStack>
          <h2
            style={{
              fontSize: "var(--text-2xl)",
              lineHeight: "var(--line-height-2xl)",
              fontWeight: "var(--font-bold)",
            }}
          >
            {archived ? "Archived Explorations" : "Explorations"}
          </h2>
          <p style={{ color: "var(--muted-foreground)" }}>
            {archived
              ? "View archived explorations."
              : "View and manage all explorations."}
          </p>
        </VStack>
        <ArchiveToggleButton />
      </HStack>
      {loading.loadExplorations ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: "1rem",
          }}
        >
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} style={{ height: "240px", width: "100%" }} />
          ))}
        </div>
      ) : explorations.explorations.length < 1 ? (
        <Empty style={{ borderColor: "var(--secondary-border)" }}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BookOpen />
            </EmptyMedia>
            <EmptyTitle>
              {archived
                ? "No Archived Explorations"
                : "No Explorations Found"}
            </EmptyTitle>
            <EmptyDescription>
              {archived
                ? "There are no archived explorations."
                : "There were no explorations found, if you believe this is an error, please refresh."}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Explorations
          data={explorations}
          onEdit={openEdit}
          onEnroll={openEnroll}
          archived={archived}
        />
      )}
      {open.edit.exploration && (
        <EditExplorationSheet
          open={open.edit.open}
          onOpenChange={(open) =>
            setOpen((prev) => ({
              ...prev,
              edit: { open, exploration: prev.edit.exploration },
            }))
          }
          setExplorations={setExplorations}
          data={{
            rotations: explorations.rotations,
            sessions: explorations.sessions,
            teachers: explorations.teachers,
          }}
          exploration={open.edit.exploration}
        />
      )}
      {open.enroll.exploration && (
        <EnrollStudentsSheet
          open={open.enroll.open}
          onOpenChange={(open) =>
            setOpen((prev) => ({
              ...prev,
              enroll: { open, exploration: prev.enroll.exploration },
            }))
          }
          exploration={open.enroll.exploration}
          students={students}
          setStudents={setStudents}
        />
      )}
    </Dashboard>
  );
};

export default AdminExplorations;
